import type { MoveInput, Settings } from './types';
const EDITABLE = 'input,select,textarea,[contenteditable]:not([contenteditable="false"])';
const CONTROL = 'button,a[href],summary,[role="button"],[role="tab"],[role="link"]';
const NAVIGATION = ['Tab', 'Space', 'Enter', 'NumpadEnter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
/** Keys the game binds during play; Tab is not one of them, so focus can always leave the canvas. */
const GAME_KEYS = ['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'F1'];
/** The title menu or any dialog (journal, result) is showing. */
export function overlayOpen(doc: Document = document) {
    const menu = doc.getElementById('menu');
    return !!(menu && !menu.hidden) || !!doc.querySelector('dialog[open]');
}
export class Input {
    keys = new Set<string>();
    yaw = 0;
    pitch = .23;
    grab = false;
    tail = false;
    grabSource: 'mouse' | 'touch' = 'mouse';
    tailSource: 'mouse' | 'touch' = 'mouse';
    touchX = 0;
    touchY = 0;
    touchJump = false;
    enabled = false;
    device: 'keyboard' | 'mouse' | 'touch' | 'gamepad' = matchMedia('(pointer:coarse)').matches ? 'touch' : 'keyboard';
    onAction: (code: string) => void = () => { };
    private padInteract = false;
    private padMenu = false;
    private padReturn = false;
    private drag = false;
    private joyId = -1;
    private joyOrigin = [0, 0];
    constructor(private canvas: HTMLCanvasElement, private settings: () => Settings) {
        window.addEventListener('keydown', e => {
            const target = e.target instanceof Element ? e.target : null;
            if (target?.closest(EDITABLE))
                return;
            this.device = 'keyboard';
            const nav = NAVIGATION.includes(e.code);
            if (nav && target?.closest(CONTROL)) {
                // A control reached by keyboard keeps native Tab/Space/Enter/arrow behaviour. One that
                // merely kept focus after a mouse click hands the keyboard back to the game.
                if (target.matches(':focus-visible') || overlayOpen()) {
                    if (!e.repeat)
                        this.onAction(e.code);
                    return;
                }
                (target as HTMLElement).blur();
            }
            if (overlayOpen()) {
                // Menus and dialogs own the keyboard: no held game keys, no swallowed navigation.
                if (e.code === 'F1')
                    e.preventDefault();
                if (!e.repeat)
                    this.onAction(e.code);
                return;
            }
            if (GAME_KEYS.includes(e.code))
                e.preventDefault();
            if (!e.repeat)
                this.onAction(e.code);
            this.keys.add(e.code);
        });
        window.addEventListener('keyup', e => this.keys.delete(e.code));
        window.addEventListener('blur', () => this.reset());
        document.addEventListener('visibilitychange', () => { if (document.hidden) this.reset(); });
        canvas.addEventListener('contextmenu', e => e.preventDefault());
        canvas.addEventListener('pointerdown', e => {
            this.device = e.pointerType === 'touch' ? 'touch' : 'mouse';
            if (e.pointerType === 'touch') {
                if (!this.enabled)
                    return;
                this.drag = true;
                canvas.setPointerCapture(e.pointerId);
                return;
            }
            if (!this.enabled)
                return;
            // Keep the release even if the pointer leaves the canvas before pointer lock engages.
            try { canvas.setPointerCapture(e.pointerId); } catch { }
            if (e.button === 0) {
                this.grab = true;
                this.grabSource = 'mouse';
            }
            if (e.button === 2) {
                this.tail = true;
                this.tailSource = 'mouse';
            }
            if (document.pointerLockElement !== canvas)
                canvas.requestPointerLock()?.catch(() => { });
        });
        const release = (e: PointerEvent) => {
            if (e.pointerType !== 'touch') {
                this.grab = false;
                this.tail = false;
            }
            if (e.target === canvas || e.type !== 'pointerup')
                this.drag = false;
        };
        window.addEventListener('pointerup', release);
        window.addEventListener('pointercancel', release);
        canvas.addEventListener('lostpointercapture', release);
        window.addEventListener('pointermove', e => {
            if (document.pointerLockElement === canvas || this.drag) {
                const sensitivity = this.settings().sensitivity * .003;
                this.yaw -= e.movementX * sensitivity;
                this.pitch = Math.max(-.55, Math.min(1.15, this.pitch + e.movementY * sensitivity * (this.settings().invertY ? -1 : 1)));
            }
        });
        const joy = document.querySelector<HTMLElement>('#joystick')!, knob = joy.firstElementChild as HTMLElement;
        joy.addEventListener('pointerdown', e => { this.device = 'touch'; this.joyId = e.pointerId; this.joyOrigin = [e.clientX, e.clientY]; joy.setPointerCapture(e.pointerId); });
        joy.addEventListener('pointermove', e => {
            if (e.pointerId !== this.joyId)
                return;
            const x = e.clientX - this.joyOrigin[0], y = e.clientY - this.joyOrigin[1], d = Math.max(44, Math.hypot(x, y));
            this.touchX = x / d;
            this.touchY = -y / d;
            knob.style.transform = `translate(${this.touchX * 34}px,${-this.touchY * 34}px)`;
        });
        const stopJoy = () => { this.joyId = -1; this.touchX = this.touchY = 0; knob.style.transform = ''; };
        joy.addEventListener('pointerup', stopJoy);
        joy.addEventListener('pointercancel', stopJoy);
        for (const [id, key] of [['touch-grab', 'grab'], ['touch-tail', 'tail'], ['touch-jump', 'touchJump']] as const) {
            const button = document.getElementById(id)!;
            button.addEventListener('pointerdown', e => { e.preventDefault(); this.device = 'touch'; button.setPointerCapture(e.pointerId); this[key] = true; if (key === 'grab') this.grabSource = 'touch'; if (key === 'tail') this.tailSource = 'touch'; });
            for (const event of ['pointerup', 'pointercancel'])
                button.addEventListener(event, e => { e.stopPropagation(); this[key] = false; });
        }
    }
    reset() { this.keys.clear(); this.grab = this.tail = this.touchJump = false; this.touchX = this.touchY = 0; }
    sample(): MoveInput {
        let x = Number(this.keys.has('KeyD')) - Number(this.keys.has('KeyA')) + this.touchX, z = Number(this.keys.has('KeyW')) - Number(this.keys.has('KeyS')) + this.touchY;
        let buttons = 0;
        if (this.grab || this.keys.has('Space'))
            buttons |= 5;
        if (this.tail || this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'))
            buttons |= 2;
        if (this.keys.has('Space') || this.touchJump)
            buttons |= 4;
        if (this.keys.has('ControlLeft'))
            buttons |= 8;
        const pad = navigator.getGamepads?.()[0];
        if (pad) {
            const dead = (n: number) => Math.abs(n) > .16 ? n : 0;
            if (pad.buttons.some(b => b.pressed) || pad.axes.some(a => Math.abs(a) > .16))
                this.device = 'gamepad';
            x += dead(pad.axes[0]);
            z -= dead(pad.axes[1]);
            this.yaw -= dead(pad.axes[2]) * .035;
            this.pitch = Math.max(-.5, Math.min(1.1, this.pitch + dead(pad.axes[3]) * .025));
            if (pad.buttons[7]?.pressed)
                buttons |= 5;
            if (pad.buttons[6]?.pressed)
                buttons |= 2;
            if (pad.buttons[0]?.pressed)
                buttons |= 4;
            if (pad.buttons[1]?.pressed)
                buttons |= 8;
            if (pad.buttons[2]?.pressed && !this.padInteract)
                this.onAction('KeyE');
            this.padInteract = !!pad.buttons[2]?.pressed;
            if (pad.buttons[9]?.pressed && !this.padMenu)
                this.onAction('Escape');
            this.padMenu = !!pad.buttons[9]?.pressed;
            if (pad.buttons[3]?.pressed && !this.padReturn)
                this.onAction('KeyR');
            this.padReturn = !!pad.buttons[3]?.pressed;
        }
        const len = Math.max(1, Math.hypot(x, z));
        x /= len;
        z /= len;
        return { x: Math.cos(this.yaw) * x + Math.sin(this.yaw) * z, z: -Math.sin(this.yaw) * x + Math.cos(this.yaw) * z, buttons, assist: this.settings().assist };
    }
}
